import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Package, TrendingUp, TrendingDown, RefreshCcw, AlertTriangle, ClipboardCheck, Trash2 } from 'lucide-react';
import StockAlertBadge from '../components/StockAlertBadge';
import { toast } from 'sonner';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StockPage = () => {
  const { getAuthHeaders, user } = useAuth();
  const [rawMaterials, setRawMaterials] = useState([]);
  const [finishedProducts, setFinishedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [resetResult, setResetResult] = useState(null);
  const [adjustments, setAdjustments] = useState([]);
  const [adjustTarget, setAdjustTarget] = useState(null); // {kind:'agglo'|'mp', ...item}
  const [adjustForm, setAdjustForm] = useState({ physical_count: '', reason: '' });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [rawRes, finishedRes, adjRes] = await Promise.all([
        axios.get(`${API}/raw-materials`, getAuthHeaders()),
        axios.get(`${API}/finished-products`, getAuthHeaders()),
        axios.get(`${API}/finished-products/adjustments?limit=20`, getAuthHeaders())
      ]);
      setRawMaterials(rawRes.data);
      setFinishedProducts(finishedRes.data);
      setAdjustments(adjRes.data);
    } catch (error) {
      console.error('Error fetching stock:', error);
      toast.error('Erreur lors du chargement des stocks');
    } finally {
      setLoading(false);
    }
  };

  const handleResetStock = async () => {
    setResetting(true);
    try {
      const response = await axios.post(`${API}/finished-products/reset-stock`, {}, getAuthHeaders());
      setResetResult(response.data);
      toast.success('Stocks recalculés avec succès !');
      fetchData(); // Refresh data
    } catch (error) {
      console.error('Error resetting stock:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors du recalcul des stocks');
    } finally {
      setResetting(false);
      setShowResetConfirm(false);
    }
  };

  const isAdmin = user?.role === 'admin';

  const openAdjustDialog = (item, kind) => {
    setAdjustTarget({ ...item, kind });
    setAdjustForm({ physical_count: (Math.round(item.current_stock * 100) / 100).toString(), reason: '' });
  };

  const handleAdjustSubmit = async (e) => {
    e.preventDefault();
    try {
      const isAgglo = adjustTarget.kind === 'agglo';
      const url = isAgglo ? `${API}/finished-products/adjust` : `${API}/raw-materials/adjust`;
      const payload = isAgglo
        ? { agglo_type: adjustTarget.type, physical_count: parseFloat(adjustForm.physical_count), reason: adjustForm.reason || null }
        : { material_id: adjustTarget.id, physical_count: parseFloat(adjustForm.physical_count), reason: adjustForm.reason || null };
      const res = await axios.post(url, payload, getAuthHeaders());
      toast.success(res.data.message);
      setAdjustTarget(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'ajustement');
    }
  };

  const deleteAdjustment = async (adj) => {
    if (!window.confirm('Supprimer cet ajustement ? Le stock sera recalculé sans lui.')) return;
    try {
      await axios.delete(`${API}/finished-products/adjustments/${adj.id}`, getAuthHeaders());
      toast.success('Ajustement supprimé');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-full">Chargement...</div>;
  }

  return (
    <div className="space-y-6" data-testid="stock-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-4xl font-bold text-[#1B3268] tracking-tight flex items-center gap-3" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
            <Package className="w-10 h-10" />
            Gestion des Stocks
          </h1>
          <p className="text-sm text-slate-600 mt-1">État des stocks en temps réel</p>
        </div>
        {isAdmin && (
          <Button
            onClick={() => setShowResetConfirm(true)}
            data-testid="reset-stock-button"
            variant="outline"
            className="border-amber-500 text-amber-600 hover:bg-amber-50 font-semibold"
          >
            <RefreshCcw className="w-4 h-4 mr-2" />
            Recalculer les Stocks
          </Button>
        )}
      </div>

      {/* Reset Result Info */}
      {resetResult && (
        <Card className="bg-green-50 border border-green-200 p-4">
          <h4 className="font-semibold text-green-800 mb-2">Résultat du recalcul :</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            {resetResult.details?.map((item) => (
              <div key={item.type} className="bg-white p-3 rounded border border-green-100">
                <p className="font-semibold text-[#1B3268]">{item.name}</p>
                <p>Productions: <span className="font-mono">{item.total_produced}</span></p>
                <p>Ventes: <span className="font-mono">{item.total_sold}</span></p>
                <p>Rebus: <span className="font-mono text-red-600">{item.total_rebus}</span></p>
                <p className="font-bold mt-1">Stock final: <span className="text-green-600">{item.current_stock}</span></p>
              </div>
            ))}
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => setResetResult(null)}
            className="mt-2 text-green-700"
          >
            Fermer
          </Button>
        </Card>
      )}

      {/* Raw Materials Stock */}
      <Card className="bg-white border border-slate-200 rounded-sm shadow-sm" data-testid="raw-materials-stock-card">
        <div className="border-b border-slate-100 p-4 bg-slate-50/50">
          <h3 className="text-xl font-semibold text-[#1B3268] flex items-center gap-2">
            <TrendingDown className="w-5 h-5" />
            Matières Premières
          </h3>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {rawMaterials.map((material) => {
              const stockKg = material.current_stock * (material.unit === 'tonne' ? 1000 : 1);
              const thresholdKg = material.alert_threshold * (material.unit === 'tonne' ? 1000 : 1);
              
              return (
                <div key={material.id} className="border border-slate-200 rounded-sm p-5 bg-slate-50" data-testid={`stock-material-${material.type}`}>
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-bold text-lg text-[#1B3268]">{material.name}</h4>
                    <StockAlertBadge current={material.current_stock} threshold={material.alert_threshold} />
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-600">Stock actuel:</span>
                      <span className="font-mono font-bold">{Math.round(material.current_stock * 100) / 100} {material.unit}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Équivalent kg:</span>
                      <span className="font-mono">{stockKg.toFixed(2)} kg</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Seuil d'alerte:</span>
                      <span className="font-mono">{material.alert_threshold} {material.unit}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Prix unitaire:</span>
                      <span className="font-mono text-[#1B3268] font-bold">{material.price_per_unit.toFixed(2)} FCFA</span>
                    </div>
                    <div className="flex justify-between pt-2 border-t border-slate-300">
                      <span className="text-slate-700 font-medium">Valeur stock:</span>
                      <span className="font-mono text-green-600 font-bold">{(material.current_stock * material.price_per_unit).toFixed(2)} FCFA</span>
                    </div>
                  </div>
                  {isAdmin && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openAdjustDialog(material, 'mp')}
                      data-testid={`adjust-material-button-${material.type}`}
                      className="w-full mt-3 border-slate-300 text-[#1B3268] rounded-sm text-xs"
                    >
                      <ClipboardCheck className="w-3.5 h-3.5 mr-1.5" />
                      Ajuster (inventaire)
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </Card>

      {/* Finished Products Stock */}
      <Card className="bg-white border border-slate-200 rounded-sm shadow-sm" data-testid="finished-products-stock-card">
        <div className="border-b border-slate-100 p-4 bg-slate-50/50">
          <h3 className="text-xl font-semibold text-[#1B3268] flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />
            Produits Finis
          </h3>
          <p className="text-xs text-slate-500 mt-1">Stock = Productions - Ventes - Rebus + Ajustements d'inventaire</p>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {finishedProducts.map((product) => (
              <div key={product.id} className="border-2 border-[#1B3268] rounded-sm p-6 bg-gradient-to-br from-[#1B3268] to-[#2a4785] text-white relative" data-testid={`stock-product-${product.type}`}>
                <h4 className="font-bold text-xl mb-3">{product.name}</h4>
                <div className="text-center">
                  <p className={`text-5xl font-bold mb-2 ${product.current_stock < 0 ? 'text-red-400' : 'text-[#FDC800]'}`}>{product.current_stock}</p>
                  <p className="text-sm text-white/80">unités en stock</p>
                </div>
                {isAdmin && (
                  <Button
                    size="sm"
                    onClick={() => openAdjustDialog(product, 'agglo')}
                    data-testid={`adjust-stock-button-${product.type}`}
                    className="w-full mt-4 bg-white/10 hover:bg-white/20 text-white border border-white/30 rounded-sm text-xs"
                  >
                    <ClipboardCheck className="w-3.5 h-3.5 mr-1.5" />
                    Ajuster (inventaire)
                  </Button>
                )}
              </div>
            ))}
          </div>
          {finishedProducts.length === 0 && (
            <div className="text-center py-12 text-slate-500">
              Aucun produit fini en stock
            </div>
          )}
        </div>
      </Card>

      {/* Adjustments history */}
      {adjustments.length > 0 && (
        <Card className="bg-white border border-slate-200 rounded-sm shadow-sm" data-testid="adjustments-history-card">
          <div className="border-b border-slate-100 p-4 bg-slate-50/50">
            <h3 className="text-xl font-semibold text-[#1B3268] flex items-center gap-2">
              <ClipboardCheck className="w-5 h-5" />
              Historique des ajustements d'inventaire
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-slate-600 text-xs uppercase">
                  <th className="p-3 text-left">Date</th>
                  <th className="p-3 text-left">Produit</th>
                  <th className="p-3 text-right">Stock avant</th>
                  <th className="p-3 text-right">Compté</th>
                  <th className="p-3 text-right">Ajustement</th>
                  <th className="p-3 text-left">Motif</th>
                  {isAdmin && <th className="p-3 text-center">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {adjustments.map((adj) => (
                  <tr key={adj.id} className="border-b last:border-0 hover:bg-slate-50" data-testid={`adjustment-row-${adj.id}`}>
                    <td className="p-3 font-mono text-xs">{new Date(adj.date).toLocaleDateString('fr-FR')}</td>
                    <td className="p-3 font-medium">{adj.agglo_name || adj.material_name}{adj.kind === 'raw_material' && <span className="ml-1.5 text-xs text-slate-400">(MP)</span>}</td>
                    <td className="p-3 text-right font-mono">{Math.round(adj.previous_stock * 100) / 100} {adj.unit || ''}</td>
                    <td className="p-3 text-right font-mono font-bold">{Math.round(adj.physical_count * 100) / 100} {adj.unit || ''}</td>
                    <td className={`p-3 text-right font-mono font-bold ${adj.delta >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      {adj.delta > 0 ? '+' : ''}{Math.round(adj.delta * 100) / 100}
                    </td>
                    <td className="p-3 text-slate-600">{adj.reason || '—'}</td>
                    {isAdmin && (
                      <td className="p-3 text-center">
                        <Button variant="ghost" size="sm" onClick={() => deleteAdjustment(adj)} data-testid={`delete-adjustment-${adj.id}`} className="h-7 w-7 p-0 text-red-500 hover:bg-red-50">
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Adjust Dialog */}
      <Dialog open={!!adjustTarget} onOpenChange={(o) => !o && setAdjustTarget(null)}>
        <DialogContent className="bg-white" data-testid="adjust-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#1B3268] flex items-center gap-2" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              <ClipboardCheck className="w-5 h-5" />
              Ajustement d'inventaire — {adjustTarget?.name}
            </DialogTitle>
            <DialogDescription className="text-slate-500 text-sm">
              Saisissez le stock réel compté physiquement : le stock sera aligné et l'écart tracé dans l'historique.
            </DialogDescription>
          </DialogHeader>
          {adjustTarget && (() => {
            const unit = adjustTarget.kind === 'mp' ? adjustTarget.unit : 'unités';
            const diff = parseFloat(adjustForm.physical_count) - adjustTarget.current_stock;
            return (
            <form onSubmit={handleAdjustSubmit} className="space-y-4">
              <div className="bg-slate-50 border border-slate-200 rounded-sm p-3 flex items-center justify-between text-sm">
                <span className="text-slate-600">Stock calculé actuel</span>
                <span className={`font-mono font-bold text-lg ${adjustTarget.current_stock < 0 ? 'text-red-600' : 'text-[#1B3268]'}`} data-testid="adjust-current-stock">
                  {Math.round(adjustTarget.current_stock * 100) / 100} {unit}
                </span>
              </div>
              <div>
                <Label>Stock réel compté (inventaire) *</Label>
                <Input
                  type="number" step={adjustTarget.kind === 'mp' ? '0.01' : '1'} required
                  value={adjustForm.physical_count}
                  onChange={(e) => setAdjustForm({ ...adjustForm, physical_count: e.target.value })}
                  data-testid="adjust-physical-count-input"
                  className="h-10 border-slate-300 rounded-sm mt-1 font-mono"
                />
                {adjustForm.physical_count !== '' && (
                  <p className="text-xs mt-1.5" data-testid="adjust-delta-preview">
                    Écart : <span className={`font-mono font-bold ${diff >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      {diff > 0 ? '+' : ''}{Math.round(diff * 100) / 100} {unit}
                    </span>
                  </p>
                )}
              </div>
              <div>
                <Label>Motif</Label>
                <Input
                  value={adjustForm.reason}
                  onChange={(e) => setAdjustForm({ ...adjustForm, reason: e.target.value })}
                  placeholder="Ex: Inventaire physique du mois, casse non déclarée..."
                  data-testid="adjust-reason-input"
                  className="h-10 border-slate-300 rounded-sm mt-1"
                />
              </div>
              <div className="flex gap-2 pt-2">
                <Button type="submit" data-testid="adjust-submit-button" className="flex-1 bg-[#1B3268] hover:bg-[#152a55] text-white rounded-sm">
                  Valider l'ajustement
                </Button>
                <Button type="button" variant="outline" onClick={() => setAdjustTarget(null)} className="flex-1 rounded-sm">Annuler</Button>
              </div>
            </form>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Reset Confirmation Dialog */}
      <AlertDialog open={showResetConfirm} onOpenChange={setShowResetConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-amber-600 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Recalculer tous les stocks ?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 mt-2">
                <span className="block">Cette action va recalculer le stock de tous les produits finis selon la formule :</span>
                <span className="block bg-slate-100 p-3 rounded-md text-center font-mono text-sm">
                  Stock = Productions - Ventes - Rebus
                </span>
                <span className="block text-green-600 font-medium">
                  ✓ Aucune donnée ne sera supprimée. Seul le stock affiché sera mis à jour.
                </span>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={resetting}>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleResetStock}
              disabled={resetting}
              className="bg-amber-500 hover:bg-amber-600 text-white"
            >
              {resetting ? 'Recalcul en cours...' : 'Recalculer'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default StockPage;
